'use client';

import type { ReactNode } from 'react';
import { Icons } from '@/lib/ui-kit/icons';
import { MoodBar } from './MoodBar';

interface SearchShellProps {
  // The actual input — a plain <input> (GIF) or <EmojiPicker.Search> (emoji).
  children: ReactNode;
  // Moods show while not actively searching; they collapse on focus/typing so
  // the input gets full width.
  showMoods: boolean;
  // Term of the selected mood (kept highlighted), or null.
  activeMood?: string | null;
  onMood: (term: string) => void;
  // Focuses the input — the magnifier is the reliable hit target since moods
  // otherwise crowd the row.
  onSearchIconClick?: () => void;
  // Clears the active mood (the leading icon becomes a back arrow while one is set).
  onBack?: () => void;
}

// Shared search-bar chrome for both picker tabs: leading search icon (or back
// arrow while a mood filter is active), the caller's input, and the quick-mood
// shortcuts.
export const SearchShell = ({
  children,
  showMoods,
  activeMood,
  onMood,
  onSearchIconClick,
  onBack,
}: SearchShellProps) => (
  <div className="flex h-10 shrink-0 items-center gap-2 rounded-[10px] border border-v2-btn-secondary-hover px-3.5">
    {activeMood ? (
      <button
        type="button"
        onClick={onBack}
        className="flex shrink-0 cursor-pointer items-center text-v2-text-secondary transition-colors hover:text-v2-text-primary"
      >
        <Icons.ArrowRight className="size-5 rotate-180" />
      </button>
    ) : (
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onSearchIconClick}
        className="flex shrink-0 cursor-text items-center text-v2-text-secondary"
      >
        <Icons.Search className="size-5" />
      </button>
    )}
    {children}
    {showMoods && <MoodBar activeTerm={activeMood} onSelect={onMood} />}
  </div>
);
